// src/pages/ActivityLog.js
import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TextField,
  Button,
  MenuItem,
  Autocomplete,
  Checkbox,
  ListItemText,
} from "@mui/material";
import { useAuth } from "../authContext";

/**
 * Activity Logs
 * - Retains your current multi-select filters, date range, details text filter, pager & UI
 * - Adds role-based scoping before filters kick in
 *
 * Visibility:
 *  Admin:   all logs for companyId
 *  Manager: logs by (manager + their members) OR tied to projects they manage OR projects of their members
 *  Member:  logs by self OR tied to projects they are member of
 */
const ActivityLog = () => {
  const { user } = useAuth();

  const [companyId, setCompanyId] = useState(null);
  const [role, setRole] = useState(null);
  const [logs, setLogs] = useState([]);

  // Filters (unchanged)
  const [selUsers, setSelUsers] = useState([]);     // array<string> (user names)
  const [selActions, setSelActions] = useState([]); // array<string>
  const [filterDetails, setFilterDetails] = useState("");
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");

  // Pager (unchanged)
  const [page, setPage] = useState(1); // 1-based
  const [rowsPerPage, setRowsPerPage] = useState(20);

  /** 1) Load current user's company & role */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.uid) return;
      const snap = await getDoc(doc(db, "users", user.uid));
      const u = snap.exists() ? snap.data() : null;
      if (!cancelled) {
        setCompanyId(u?.companyId || null);
        setRole(u?.role || null);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.uid]);

  /** Helper: fetch ids of members for a manager (managerMemberLinks) */
  const fetchManagerMemberIds = async (cid, managerId) => {
    const qy = query(
      collection(db, "managerMemberLinks"),
      where("companyId", "==", cid),
      where("managerId", "==", managerId)
    );
    const snap = await getDocs(qy);
    return snap.docs.map(d => d.data()?.memberId).filter(Boolean);
  };

  /** Helper: fetch projects a member is linked to (projectMemberships) */
  const fetchMemberProjectIds = async (cid, memberId) => {
    const qy = query(
      collection(db, "projectMemberships"),
      where("companyId", "==", cid),
      where("memberId", "==", memberId)
    );
    const snap = await getDocs(qy);
    return snap.docs.map(d => d.data()?.projectId).filter(Boolean);
  };

  /** Helper: fetch projects managed by a manager (projects.managerId == uid) */
  const fetchManagedProjectIds = async (cid, managerId) => {
    const qy = query(
      collection(db, "projects"),
      where("companyId", "==", cid),
      where("managerId", "==", managerId)
    );
    const snap = await getDocs(qy);
    return snap.docs.map(d => d.id);
  };

  /**
   * 2) Load company logs, then apply role-based scope:
   *     - Admin: pass-through
   *     - Manager: keep if (log.userId ∈ allowedUserIds) OR (log.projectId ∈ allowedProjectIds)
   *     - Member:  keep if (log.userId == me) OR (log.projectId ∈ myProjectIds)
   *
   *  We intentionally do NOT exclude general, unlinked logs if they were authored by an allowed user.
   */
  useEffect(() => {
    if (!companyId || !role) return;

    (async () => {
      // Load all company logs (same as before)
      const qy = query(collection(db, "activityLogs"), where("companyId", "==", companyId));
      const snapshot = await getDocs(qy);
      let companyLogs = snapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          const ta = a.timestamp?.toMillis?.() ?? a.timestamp?.seconds ?? 0;
          const tb = b.timestamp?.toMillis?.() ?? b.timestamp?.seconds ?? 0;
          return tb - ta; // desc
        });

      if (role === "Admin") {
        setLogs(companyLogs);   // no additional scoping
        return;
      }

      // Build scopes
      const me = user?.uid;
      let allowedUserIds = new Set([me]);   // always include self
      let allowedProjectIds = new Set();

      if (role === "Manager") {
        // 1) members managed by me
        const memberIds = await fetchManagerMemberIds(companyId, me);
        memberIds.forEach(id => allowedUserIds.add(id));

        // 2) projects I manage
        const myManaged = await fetchManagedProjectIds(companyId, me);
        myManaged.forEach(pid => allowedProjectIds.add(pid));

        // 3) projects of my members
        if (memberIds.length) {
          const uniq = Array.from(new Set(memberIds));
          const chunk = async (arr, size) => {
            const out = [];
            for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
            return out;
          };
          for (const group of await chunk(uniq, 10)) {
            // parallel fetch for small groups to keep it snappy
            const results = await Promise.all(group.map(id => fetchMemberProjectIds(companyId, id)));
            results.flat().forEach(pid => allowedProjectIds.add(pid));
          }
        }
      }

      if (role === "Member") {
        // projects I'm a member of
        const myProjects = await fetchMemberProjectIds(companyId, me);
        myProjects.forEach(pid => allowedProjectIds.add(pid));
      }

      // Apply scope
      companyLogs = companyLogs.filter((log) => {
        const byAllowedUser = allowedUserIds.has(log.userId);
        const inAllowedProject = log.projectId && allowedProjectIds.has(log.projectId);
        if (role === "Manager") {
          // manager sees: by self/members OR in a managed/member project
          return byAllowedUser || inAllowedProject;
        }
        // member sees: by self OR in their member projects
        return byAllowedUser || inAllowedProject;
      });

      setLogs(companyLogs);
    })();
  }, [companyId, role, user?.uid]);

  /* ---------- Your existing filter pipeline (unchanged) ---------- */

  const inRangeAndDetails = useMemo(() => {
    return logs.filter((log) => {
      const detailsMatch =
        !filterDetails ||
        (log.details || "").toLowerCase().includes(filterDetails.toLowerCase());

      const ts = log.timestamp?.toDate?.();
      const startOk = !filterStartDate || (ts && ts >= new Date(filterStartDate));
      const endOk =
        !filterEndDate || (ts && ts <= new Date(filterEndDate + "T23:59:59"));

      return detailsMatch && startOk && endOk;
    });
  }, [logs, filterDetails, filterStartDate, filterEndDate]);

  const usersScoped = useMemo(() => {
    let base = inRangeAndDetails;
    if (selActions.length) {
      const s = new Set(selActions);
      base = base.filter((l) => s.has(l.action || ""));
    }
    return Array.from(
      new Set(base.map((l) => (l.userName || "-").trim()).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b));
  }, [inRangeAndDetails, selActions]);

  const actionsScoped = useMemo(() => {
    let base = inRangeAndDetails;
    if (selUsers.length) {
      const s = new Set(selUsers.map((u) => u.trim()));
      base = base.filter((l) => s.has((l.userName || "-").trim()));
    }
    return Array.from(
      new Set(base.map((l) => (l.action || "").trim()).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b));
  }, [inRangeAndDetails, selUsers]);

  // keep selections valid
  useEffect(() => {
    if (selUsers.length) {
      const allowed = new Set(usersScoped);
      const next = selUsers.filter((u) => allowed.has(u));
      if (next.length !== selUsers.length) setSelUsers(next);
    }
  }, [usersScoped]); // eslint-disable-line

  useEffect(() => {
    if (selActions.length) {
      const allowed = new Set(actionsScoped);
      const next = selActions.filter((a) => allowed.has(a));
      if (next.length !== selActions.length) setSelActions(next);
    }
  }, [actionsScoped]); // eslint-disable-line

  const filteredLogs = useMemo(() => {
    return inRangeAndDetails.filter((log) => {
      const userOk =
        !selUsers.length ||
        selUsers.includes((log.userName || "-").trim());
      const actionOk =
        !selActions.length ||
        selActions.includes((log.action || "").trim());
      return userOk && actionOk;
    });
  }, [inRangeAndDetails, selUsers, selActions]);

  const totalRows = filteredLogs.length;
  const pageCount = Math.max(1, Math.ceil(totalRows / rowsPerPage));
  useEffect(() => { if (page > pageCount) setPage(pageCount); }, [page, pageCount]);

  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, totalRows);
  const pagedLogs = useMemo(
    () => filteredLogs.slice(startIndex, endIndex),
    [filteredLogs, startIndex, endIndex]
  );

  const clearFilters = () => {
    setSelUsers([]);
    setSelActions([]);
    setFilterDetails("");
    setFilterStartDate("");
    setFilterEndDate("");
    setPage(1);
  };

  /* ---------- Render (unchanged columns/filters) ---------- */
  return (
    <Box p={4}>
      <Typography variant="h5" gutterBottom>Activity Logs</Typography>

      <Paper>
        <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
          {/* User (multi-select) */}
          <Autocomplete
            size="small"
            multiple
            sx={{ width: 220 }}
            options={usersScoped}
            value={selUsers}
            onChange={(_, v) => { setSelUsers(v); setPage(1); }}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox checked={selected} />
                <ListItemText primary={option} />
              </li>
            )}
            renderInput={(params) => <TextField {...params} label="Filter by user" />}
          />

          {/* Action (multi-select) */}
          <Autocomplete
            size="small"
            multiple
            sx={{ width: 220 }}
            options={actionsScoped}
            value={selActions}
            onChange={(_, v) => { setSelActions(v); setPage(1); }}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox checked={selected} />
                <ListItemText primary={option} />
              </li>
            )}
            renderInput={(params) => <TextField {...params} label="Filter by action" />}
          />

          {/* Details contains */}
          <TextField
            size="small"
            label="Filter by details"
            value={filterDetails}
            onChange={(e) => { setFilterDetails(e.target.value); setPage(1); }}
            sx={{ width: 260 }}
          />

          {/* Date range */}
          <TextField
            size="small"
            type="date"
            label="Start date"
            InputLabelProps={{ shrink: true }}
            value={filterStartDate}
            onChange={(e) => { setFilterStartDate(e.target.value); setPage(1); }}
          />
          <TextField
            size="small"
            type="date"
            label="End date"
            InputLabelProps={{ shrink: true }}
            value={filterEndDate}
            onChange={(e) => { setFilterEndDate(e.target.value); setPage(1); }}
          />

          <Button onClick={clearFilters}>Clear filters</Button>
        </Box>

        {/* Table */}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell style={{ width: 220 }}>User</TableCell>
              <TableCell style={{ width: 160 }}>Action</TableCell>
              <TableCell>Details</TableCell>
              <TableCell style={{ width: 180 }}>Time</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedLogs.map((l) => (
              <TableRow key={l.id}>
                <TableCell>{l.userName || "-"}</TableCell>
                <TableCell>{l.action || "-"}</TableCell>
                <TableCell>{l.details || "-"}</TableCell>
                <TableCell>
                  {l.timestamp?.toDate?.()?.toLocaleString?.() ||
                    l.timestamp || "-"}
                </TableCell>
              </TableRow>
            ))}
            {!pagedLogs.length && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No rows
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Simple pager */}
        <Box display="flex" justifyContent="space-between" p={2}>
          <TextField
            select
            size="small"
            label="Rows per page"
            value={rowsPerPage}
            onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}
            sx={{ width: 140 }}
          >
            {[10, 20, 50, 100].map(n => (
              <MenuItem key={n} value={n}>{n}</MenuItem>
            ))}
          </TextField>

          <Box display="flex" gap={1} alignItems="center">
            <Button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
            <Typography variant="body2">Page {page}</Typography>
            <Button disabled={startIndex + rowsPerPage >= totalRows} onClick={() => setPage(p => p + 1)}>Next</Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default ActivityLog;
